import React from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Link,
  Chip,
  useMediaQuery,
} from "@mui/material";
import {
  Visibility,
  Psychology,
  AdminPanelSettings,
  Security,
  Shield,
  Balance,
  Assignment,
  TrendingUp,
  ContactSupport,
} from "@mui/icons-material";

const AIAct = ({ theme, styles, frontPage = false }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const aiActItems = [
    {
      icon: <Visibility sx={{ fontSize: 40, color: "primary.main" }} />,
      title: frontPage
        ? "Transparens om AI-användning"
        : "Transparens om AI-bedömning",
      description: frontPage
        ? "Vi använder AI för att generera olika typer av pedagogiskt material så som lektioner, quiz, flashcards, podcasts och lektionsplaneringar."
        : "Vi använder AI för att analysera och bedöma elevtexter utifrån matriser som du själv kan skapa och anpassa utifrån uppgiften.",
    },
    {
      icon: <Balance sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Mänsklig översyn enligt AI-förordningen",
      description: frontPage
        ? "Som lärare har du alltid sista ordet. AI:n ger bara ett förslag – det är du som ansvarar för det slutliga materialet. Vi förespråkar 80-20-principen. Detta är ett tydligt krav i AI-förordningen."
        : "Som lärare har du alltid sista ordet. AI:n ger bara ett förslag – det är du som ansvarar för den slutliga bedömningen. Detta är ett tydligt krav i AI-förordningen.",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Full insyn och dokumentation",
      description: frontPage
        ? "Allt som genereras av AI loggas och kan granskas i efterhand. Du kan alltid se hur AI:n resonerat och på vilka grunder det genererats, helt i linje med AI-förordningen."
        : "Alla AI-bedömningar loggas och kan granskas i efterhand. Du kan alltid se hur AI:n resonerat och på vilka grunder beslut tagits, helt i linje med AI-förordningen.",
    },

    {
      icon: <Psychology sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Hur fungerar vår AI-modell?",
      description: frontPage
        ? "Vår AI-modell är särskilt utvecklad och validerad för att generera pedagogiskt material med hög precision och rättvisa."
        : "Vår AI-modell är särskilt utvecklad och validerad för att förstå och bedöma elevtexter enligt dina valda kriterier med hög precision och rättvisa.",
    },
    {
      icon: <AdminPanelSettings sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Du har alltid kontrollen",

      description: frontPage
        ? "Du kan alltid granska, ändra och radera det som vår AI genererar. Du har således full kontroll över innehållet som genereras."
        : "Du kan alltid granska, ändra och godkänna AI:ns omdöme innan det sparas. Betrakta det som en preliminär bedömning där du alltid har sista ordet.",
    },
    {
      icon: <Security sx={{ fontSize: 40, color: "primary.main" }} />,

      title: frontPage
        ? "Säker lagring av elevdata"
        : "Säker lagring av elevtexter",
      description: frontPage
        ? "När en elev exempelvis svarar på en fråga i ett quiz så lagras detta säkert och anonymt i vår databas så länge du själv vill. Du kan när som helst ta bort alla elevdata permanent."
        : "Alla elevtexter lagras säkert och anonymt i vår databas så länge du själv vill. Du kan när som helst ta bort alla texter permanent.",
    },
    {
      icon: <Shield sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Dataskydd enligt GDPR",
      description: frontPage
        ? "Vi skyddar elevdata och personuppgifter enligt GDPR. Inga uppgifter delas med obehöriga och du har alltid rätt till insyn och radering."
        : "Vi skyddar elevtexter och personuppgifter enligt GDPR. Inga uppgifter delas med obehöriga och du har alltid rätt till insyn och radering.",
    },
    {
      icon: <Balance sx={{ fontSize: 40, color: "primary.main" }} />,
      title: frontPage
        ? "Mångfald och inkludering"
        : "Rättvisa och likvärdighet",
      description: frontPage
        ? "Vår AI är programmerad att ta hänsyn till mångfald och inkludering när den skapar innehåll. Men som lärare är det viktigt att ändå granska och säkerställa att materialet speglar olika perspektiv, bakgrunder och erfarenheter."
        : "Vår AI ger konsekvent bedömning baserat på samma kriterier för alla elever, vilket eliminerar subjektiva variationer.",
    },
    ...(frontPage
      ? []
      : [
          {
            icon: <Assignment sx={{ fontSize: 40, color: "primary.main" }} />,
            title: "Motivera bedömningen",
            description:
              "AI-bedömningen utgår från den matris du själv skapat. Detta innebär att du alltid kan motivera bedömningen utifrån dina egna pedagogiska kriterier gentemot elever, föräldrar och skolledning.",
          },
        ]),
    {
      icon: <ContactSupport sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Frågor eller synpunkter?",
      description: frontPage
        ? "Du är alltid välkommen att kontakta oss på info@studera.ai om du vill veta mer om hur vi förhåller oss till AI-förordningen."
        : "Du är alltid välkommen att kontakta oss på info@studera.ai om du vill veta mer om hur AI-bedömningen fungerar.",
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ bgcolor: "background.paper", py: 15 }}>
      <Box textAlign="center" mb={8}>
        <Chip
          label={<span>AI-förordningen</span>}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            fontWeight: "600",
            fontSize: "0.8rem",
            padding: "2px 14px",
            borderRadius: "16px",
            "& .MuiChip-label": {
              padding: "0 4px",
            },
          }}
        />

        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: { xs: 4, md: 2 },
            fontSize: { xs: "2rem", md: "2.8rem" },
            textAlign: "center",
            mt: 2,
          }}
        >
          Så följer vi AI-förordningen
        </Typography>

        <Typography
          variant="subtitle"
          sx={{
            color: "text.secondary",
            fontSize: { xs: "1rem", md: "1.3rem" },
            lineHeight: 1.7,
            whiteSpace: "pre-line",
            display: "block",
            width: "100%",
            textAlign: "center",
            mb: 5,
          }}
        >
          Vi följer EU:s{" "}
          <Link
            href="https://eur-lex.europa.eu/legal-content/SV/TXT/PDF/?uri=OJ:L_202401689"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              fontWeight: 400,
              textDecoration: "none",

              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            AI-förordning
          </Link>{" "}
          för att säkerställa trygg och ansvarsfull AI-användning.
          <br />
          <span style={{ fontWeight: 500 }}>
            Vi sätter läraren i centrum och AI:n är bara ett stöd – precis som
            lagen kräver.
          </span>
        </Typography>
      </Box>

      <Grid
        container
        spacing={isMobile ? 0 : 3}
        sx={{ width: { xs: "100%", md: "95%" }, margin: "0 auto" }}
      >
        {aiActItems.map((item, index) => (
          <Grid item xs={12} md={4} key={index} sx={{ mb: { xs: 3, md: 0 } }}>
            <Box
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? `hsl(${250 + index * 5}, 25%, ${18 + index * 2}%)`
                    : `hsl(${250 + index * 5}, 60%, ${96 - index * 1}%)`,
                p: 4,
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
                border: "1px solid #ac73ff3d",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                },
              }}
            >
              <Box sx={{ mb: { xs: 1, md: 2 } }}>{item.icon}</Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "600",
                  fontSize: { xs: "1.2rem", md: "1.3rem" },
                  mb: 1,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                }}
              >
                {item.description}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default AIAct;
